<template>
  <section class="voucher-box-edit modal-backdrop">
    <div
      class="modal-container standard-size"
      role="dialog"
      aria-labelledby="modalTitle"
      aria-describedby="modalDescription"
    >
      <!-- Header -->
      <div class="modal-container__header">
        <h2 class="h1">EDIT VOUCHER BOX</h2>
        <div style="cursor: pointer" @click="close()">
          <v-icon name="times" scale="1.5" />
        </div>
      </div>

      <language-adaptive-select
        @change="toggleLanguage"
        :languageTypes="languageTypes"
        :localLanguageTypesIndex="localLanguageTypesIndex"
      />

      <div
        class="modal-container__content"
        v-for="(languageType, key) in languageTypes"
        :key="key"
        v-show="localLanguageTypesIndex === key"
      >
        <!-- Box Content -->
        <div class="input-group">
          <h3 class="h2">Box Content</h3>

          <GlobalInputTextfield
            label="Title"
            :inputId="'title' + key"
            v-model="box.titles[localLanguageTypesIndex]"
            maxlength="30"
          />
          <GlobalInputTextfield
            label="Text"
            :inputId="'text' + key"
            v-model="box.texts[localLanguageTypesIndex]"
            maxlength="60"
          />

          <div class="icon-image-container">
            <div class="icon-image-container__button">
              <button class="button-neutral" @click="showImageModal = true">
                Choose Image
              </button>
              <ImageManager
                v-if="showImageModal"
                :isModal="true"
                @changed="onSelectedImage"
                @close="showImageModal = false"
              />
            </div>
            <div class="icon-image-container__image">
              <img :src="box.iconImage" />
            </div>
          </div>

          <GlobalInputTextfield
            label="Button Text"
            :inputId="'ButtonText' + key"
            v-model="box.buttons[localLanguageTypesIndex]"
            maxlength="10"
          />
          <GlobalInputTextfield
            label="Confirm Button Text"
            :inputId="'ConfirmButtonText' + key"
            v-model="box.buttonsConfirm[localLanguageTypesIndex]"
            maxlength="20"
          />
        </div>

        <!-- Colors -->
        <div class="input-group">
          <h3 class="h2 colorPickerWrapper">
            Colors
            <div
              v-if="showTextColorPicker"
              class="colorOuterWrap"
              :class="{ changedPickerPosition: changedPickerPosition }"
            >
              <div class="colorInnerWrap">
                <chrome-picker v-model="colors[activeColorPicker]" />
                <button
                  class="color-picker-button"
                  v-on:click="closeColorPicker()"
                >
                  Select
                </button>
                <button
                  class="closeBtn color-picker-button"
                  v-on:click="closeColorPicker()"
                >
                  Close
                </button>
              </div>
            </div>
          </h3>

          <div class="color-input-wrapper" @click="showColorPicker('boxColor')">
            <GlobalInputTextfield
              label="Box Color"
              :inputId="'boxColor' + key"
              v-model="box.boxColor"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.boxColor }"
            ></div>
          </div>
          <div class="color-input-wrapper" @click="showColorPicker('boxText')">
            <GlobalInputTextfield
              label="Text Color"
              :inputId="'textColor' + key"
              v-model="box.boxText"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.boxText }"
            ></div>
          </div>
          <div class="color-input-wrapper" @click="showColorPicker('btnColor')">
            <GlobalInputTextfield
              label="Button Color"
              :inputId="'btnColor' + key"
              v-model="box.btnColor"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.btnColor }"
            ></div>
          </div>
          <div
            class="color-input-wrapper"
            @click="showColorPicker('btnTextColor')"
          >
            <GlobalInputTextfield
              label="Button Text Color"
              :inputId="'btnTextColor' + key"
              v-model="box.btnTextColor"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.btnTextColor }"
            ></div>
          </div>
        </div>

        <!-- Button Container -->
        <div class="button-container">
          <button class="button-neutral" @click="btnCancel()">Cancel</button>
          <button class="button-positive" @click="btnUpdate()">Save</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { db, firestore } from "@/firebase";
import { Sketch } from "vue-color";
import ImageManager from "@/components/MediaPage/MediaImages";
import languageCookieMixin from "@/mixins/languageCookieMixin";
import newLanguageTypesFormatMixin from '@/mixins/newLanguageTypesFormatMixin';
import GlobalInputTextfield from "@/components/GlobalComponents/GlobalInputTextfield";
import LanguageAdaptiveSelect from '@/components/Ui/LanguageAdaptiveSelect';

//This is the edit menu for a voucher box
export default {
  name: "VoucherEdit",
  components: {
    // ButtonGlobal,
    // CloseIcon,
    ImageManager,
    GlobalInputTextfield,
    LanguageAdaptiveSelect,
    "chrome-picker": Sketch,
  },
  mixins: [languageCookieMixin, newLanguageTypesFormatMixin],
  props: {
    //The selected voucher box
    box: Object,
    //The complete list of the boxes
    completeBoxArray: Array,
    //The index of the selected box
    selectedBoxIndex: Number,
  },
  data() {
    return {
      selectedBoxInfo: Object.assign({}, JSON.parse(JSON.stringify(this.box))),
      selectedLanguage: "none",
      showImageModal: false,
      colors: {
        boxText: {
          hex: "#194d33",
        },
        boxColor: {
          hex: "#194d33",
        },
        btnTextColor: {
          hex: "#194d33",
        },
        btnColor: {
          hex: "#194d33",
        },
      },
      showTextColorPicker: false,
      activeColorPicker: "",
      localLanguageTypesIndex: 0,
      languageTypes: [],
    };
  },
  computed: {
    secondLanguage() {
      return (
        !this.$isSingleLanguage &&
        this.selectedLanguage === this.$settingsSecondLanguageType
      );
    },
  },
  watch: {
    colors: {
      handler() {
        this.box[this.activeColorPicker] = this.colors[
          this.activeColorPicker
        ].hex;
      },
      deep: true,
    },
    selectedBoxInfo: {
      deep: true,
      handler() {
        this.selectedBoxInfo.title = this.selectedBoxInfo.titles[0];
        this.selectedBoxInfo.text = this.selectedBoxInfo.texts[0];
        this.selectedBoxInfo.button = this.selectedBoxInfo.buttons[0];
        this.selectedBoxInfo.buttonConfirm = this.selectedBoxInfo.buttonsConfirm[0];
        if (!this.$isSingleLanguage) {
          this.selectedBoxInfo.titleSec = this.selectedBoxInfo.titles[1];
          this.selectedBoxInfo.textSec = this.selectedBoxInfo.texts[1];
          this.selectedBoxInfo.buttonSec = this.selectedBoxInfo.buttons[1];
          this.selectedBoxInfo.buttonConfirmSec = this.selectedBoxInfo.buttonsConfirm[1];
        }
      },
    },
  },
  async created() {
    db.ref(`users/${this.$userId}/languageTypesIndex`).on("value", (data) => {
      this.localLanguageTypesIndex = data.val();
    });

    this.languageTypes = await this._getCorrectHotelLanguages(this.$hotelId);
    // Box can be without this fields, 'cause we need to init them.
    this.box.titles = this.box.titles || [];
    this.box.texts = this.box.texts || [];
    this.box.buttons = this.box.buttons || [];
    this.box.buttonsConfirm = this.box.buttonsConfirm || [];

    // Create box as if it was controlledby languageIndex in firebase
    this.selectedBoxInfo.titles = {
      0: this.selectedBoxInfo.title,
      1: this.selectedBoxInfo.titleSec,
    };

    this.selectedBoxInfo.texts = {
      0: this.selectedBoxInfo.text,
      1: this.selectedBoxInfo.textSec,
    };

    this.selectedBoxInfo.buttons = {
      0: this.selectedBoxInfo.button,
      1: this.selectedBoxInfo.buttonSec,
    };

    this.selectedBoxInfo.buttonsConfirm = {
      0: this.selectedBoxInfo.buttonConfirm,
      1: this.selectedBoxInfo.buttonConfirmSec,
    };
  },
  mounted: function () {
    document.addEventListener("keydown", this.closeWindow);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.closeWindow);
  },
  methods: {
    //@vuese
    //Triggered when the menu is closed or the cancel button is pressed
    //@arg The argument is a string value representing the action
    close() {
      this.$emit("close");
    },
    //@vuese
    //Used for displaying the number of characters
    //@arg The argument it's a string representing the user input
    charactersRemaining(string) {
      if (string) {
        return string.length;
      } else {
        return 0;
      }
    },
    closeWindow(e) {
      if (e.keyCode == 27) {
        this.showTextColorPicker = false;
        this.close();
      }
    },
    closeColorPicker() {
      this.showTextColorPicker = false;
    },
    //@vuese
    //Used for choosing the colors for specific atributtes such as box color, text color
    //@arg The argument it's a string vaulue representing the atributte that the changes are based on
    showColorPicker(activeBox) {
      if (activeBox === "btnTextColor" || activeBox === "btnColor") {
        this.changedPickerPosition = true;
      } else {
        this.changedPickerPosition = false;
      }

      this.showTextColorPicker = true;
      this.activeColorPicker = activeBox;
      this.colors[activeBox].hex = this.box[activeBox];
    },
    //@vuese
    //Used for updating the databases with the changes made
    //@arg The arguments are the object representing the  selected box and the array with  the complete list of boxes
    async btnUpdate() {
      this.selectedBoxInfo.title = this.box.titles[0];
      this.selectedBoxInfo.titleSec = this.box.titles[1];

      this.selectedBoxInfo.text = this.box.texts[0];
      this.selectedBoxInfo.textSec = this.box.texts[1];

      this.selectedBoxInfo.button = this.box.buttons[0];
      this.selectedBoxInfo.buttonSec = this.box.buttons[1];

      this.selectedBoxInfo.buttonConfirm = this.box.buttonsConfirm[0];
      this.selectedBoxInfo.buttonConfirmSec = this.box.buttonsConfirm[1];

      this.completeBoxArray[this.selectedBoxIndex] = this.box;

      await db
        .ref(`hotels/${this.$customerId}/${this.$hotelId}/boxes/`)
        .set(this.completeBoxArray);

      await firestore
        .doc(
          `VoucherStatistics/${this.$hotelId}/Vouchers/${this.box.id}`
        )
        .update({ Name: this.box.title });

      this.$toastEvents.push({
        type: "positive",
        title: "Success",
        content: "The box has been updated",
        id: +new Date(),
      });

      this.close();
    },
    btnCancel() {
      this.close();
    },
    onSelectedImage(imageUrl) {
      this.box.iconImage = imageUrl;
    },
    toggleLanguage(indexClicked) {
      this.localLanguageTypesIndex = Number(indexClicked);
    },
  },
};
</script>

<style scoped>
.input-group .h2 {
  margin-top: 24px;
  margin-bottom: 16px;
}

.icon-image-container {
  display: flex;
  margin-bottom: 20px;
}

.icon-image-container__button {
  flex-grow: 0;
  flex-shrink: 0;
}

.icon-image-container__button button {
  width: 180px;
}

.icon-image-container__image {
  width: 100%;
  display: flex;
  margin-left: 16px;
  height: 120px;
  background-color: var(--grey-light);
  border-radius: var(--border-radius);
}

.icon-image-container__image img {
  width: 100%;
  border-radius: var(--border-radius);
  object-fit: contain;
}

.color-input-wrapper {
  position: relative;
}

.color-indicator {
  width: 28px;
  height: 28px;
  position: absolute;
  z-index: 1;
  top: 8px;
  right: 8px;
  border-radius: var(--border-radius);
  border: 2px solid var(--grey);
}

.colorPickerWrapper {
  position: relative;
}

.colorOuterWrap {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
}

.colorInnerWrap button {
  position: absolute;
  bottom: 5px;
  left: 5px;
  background-color: #48b9ba;
  color: #fff;
  cursor: pointer;
  width: 70px;
  height: 25px;
  border: none;
  border-radius: 5px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
  transition: 0.1s ease-in;
}
.colorInnerWrap button:hover {
  background-color: #49a39c;
}
.colorInnerWrap button.closeBtn {
  left: 85px;
  background-color: #894e99;
}
</style>
